import * as lastStateActions from './actions';
import * as api from '../../api';
import { message } from '@myua/kit';
import { parseLocationStateId } from './model';
import { parse } from './versions/parse';
import { getLastStateAppliedId, getStateInfo } from './selectors';
import { getPermissions } from '../app/selectors';
import { ListenerApi, takeLatest } from '../../listenerMiddleware';
import { Permission } from '../../../general/model';
import { matchPath } from 'react-router-dom';
import { routes } from '../router/model';

export const lastStateListeners = () => {
  watchSaveRequested();
};

export async function loadAndApplyLastState({
  getState,
  dispatch,
  signal,
  initial,
}: Pick<ListenerApi, 'getState' | 'dispatch' | 'signal'> & { initial: boolean }) {
  // Возвращаем true, если действительно меняем стейт.
  // Возвращаем false, если ничего не меняется.

  const permissions = getPermissions(getState());
  if (!permissions[Permission['fs.assetlab_access']]) {
    return false;
  }

  const parsedId = parseLocationStateId();
  const appliedId = getLastStateAppliedId(getState());

  if (parsedId !== null && parsedId === appliedId) {
    // state id уже был применен ранее.
    return false;
  }

  // В момент срабатывания кода в стейте может лежать старое значение page.
  // Поэтому, чтобы определить, шаренная ли это страница, смотрим location
  const isSharingPage = Boolean(
    matchPath((window.location.hash || '').substring(1), routes.sharing),
  );

  if (isSharingPage && parsedId === null) {
    // Юзер зашел на шаренную страницу, при этом state id не указан

    if (initial) {
      // Если это изначальный запуск приложения, то ничего делать не надо.
      return false;
    }

    // Если юзер попал сюда через кнопку "Назад" в браузере, то надо сбросить стейт,
    // чтобы состояние было аналогично изначальному запуску приложения.
    dispatch(lastStateActions.applied());
    return true;
  }

  if (!isSharingPage && parsedId === null && !initial) {
    // Такое возможно, когда залогиненный юзер из шаренной страницы перешел на другую страницу.
    // Например, он нажал Catalog или выбрал проект в сайдбаре и пр.
    // Не загружаем стейт, а ориентируемся на текущее состояние приложения, которое выставил юзер.
    return false;
  }

  const result = await api.loadLastState(parsedId);
  if (signal.aborted) {
    return false;
  }

  if (!result.success) {
    // Ошибка загрузки стейта.
    if (parsedId === null) {
      // Если у юзера не было ни одного сохраненного стейта, то сервер ожидаемо вернет ошибку.
      // Ничего не говорим юзеру.
    } else {
      void message.error('Last state has not been loaded');
    }
    return false;
  }

  const lastState = parse(result.data.response);
  if (!lastState) {
    // С бэка пришел какой-то невалидный стейт.
    void message.error('Invalid last state');
    return false;
  }

  // Все OK. Применяем новый стейт.
  dispatch(lastStateActions.applied(lastState));

  if (parsedId === null) {
    // В адресной строке не было state id, выставляем его.
    const url = new URL(window.location.href);
    url.searchParams.set('state_id', String(lastState.id));
    window.history.replaceState(null, '', url.href);
  }

  return true;
}

function watchSaveRequested() {
  takeLatest(lastStateActions.saveRequested, async (action, { getState, dispatch, signal }) => {
    const permissions = getPermissions(getState());
    if (!permissions[Permission['fs.assetlab_access']]) {
      return;
    }

    const payload = getStateInfo(getState());
    const result = await api.saveLastState(payload);
    if (signal.aborted) {
      return;
    }
    if (result.success) {
      const url = new URL(window.location.href);
      const id = result.data.response.id;
      url.searchParams.set('state_id', String(id));
      window.history.pushState(null, '', url.href);
      dispatch(lastStateActions.saved({ id }));
    }
  });
}
